// store/v3/session.ts — QR session state machine (SPEC §3 SessionStoreApi, §7)
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { QrToken } from '~/types/v3/token'
import type { SessionEndReason, SessionStatus } from '~/types/v3/session'
import type { SessionStoreApi } from '~/types/v3/stores'
import { cartKeyOf } from '~/types/v3/cache'
import { clearSession, loadEndReason, loadSession, saveSession } from '~/services/v3/cache/sessionStorage'
import { isTokenExpired, tokenExpiresAt } from '~/utils/v3/time'
import { setSessionContext } from '~/services/v3/monitoring'

export const useSessionStore = defineStore('v3-session', () => {
  const status = ref<SessionStatus>('idle')
  const token = ref<QrToken | null>(null)
  const endReason = ref<SessionEndReason | null>(null)

  const storeId = computed<string | null>(() => token.value?.storeId ?? null)
  const table = computed<string | null>(() => token.value?.table ?? null)
  const qrcodeId = computed<string | null>(() => token.value?.qrcodeId ?? null)
  const settingId = computed<string | null>(() => token.value?.settingId ?? null)

  const cartKey = computed<string | null>(() => {
    const s = storeId.value
    const q = qrcodeId.value
    return s && q ? cartKeyOf(s, q) : null
  })

  const expiresAt = computed<number | null>(() => tokenExpiresAt(token.value))

  /**
   * Rehydrates the session from localStorage. Idempotent: a session already in
   * memory (or one that ended in this tab) is never resurrected.
   * Returns true when a token is present afterwards.
   */
  function restore(): boolean {
    if (status.value === 'ended') return token.value !== null
    if (token.value) return true

    const persisted = loadSession()
    if (!persisted) {
      if (!endReason.value) endReason.value = loadEndReason()
      return false
    }

    token.value = persisted.token
    status.value = 'active'
    endReason.value = null
    setSessionContext(persisted.token)
    return true
  }

  /** Starts a guest session: persists the token and marks the session active. */
  function start(next: QrToken): void {
    if (next.type !== 'guest') {
      throw new Error('[v3/session] start() requires a guest token')
    }
    token.value = next
    status.value = 'active'
    endReason.value = null
    saveSession(next)
    setSessionContext(next)
  }

  function isExpired(now: number = Date.now()): boolean {
    return isTokenExpired(token.value, now)
  }

  /**
   * Ends the session: clears persistence and notifies the other tabs.
   * For 'switch' nothing is kept in memory (a new token follows immediately);
   * for every other reason the token stays in memory so the expired page and
   * the route middleware can still tell what happened. Persisted carts of other
   * QR codes are never touched here.
   */
  function end(reason: SessionEndReason): void {
    status.value = 'ended'
    endReason.value = reason
    if (reason === 'switch') token.value = null
    clearSession(reason)
    setSessionContext(null)
  }

  return {
    status,
    token,
    endReason,
    storeId,
    table,
    qrcodeId,
    settingId,
    cartKey,
    expiresAt,
    restore,
    start,
    isExpired,
    end,
  } satisfies SessionStoreApi
})

export type SessionStore = ReturnType<typeof useSessionStore>
